import { useCallback, useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  areFiltersPausedSF,
  buildTypeOrmCalendarDatesCriteria,
  DatesUnit,
  filterCriteriaSF,
  fuzzyServerSearch,
  getSpecificSearchPatterMatch,
  getStarredDisplayModeWhere,
  GROUPING_GRID_NO_GROUPS,
  IExtendableDto,
  IGroupValuePaginationState,
  IPaginationChangeEvent,
  ISearchCriteriaGroupValue,
  isEntityClientSideWithServerDatesFiltered,
  isEntityServerSidePaginated,
  isSearchboxFilteringIgnoresDatesSF,
  isSearchboxPausedSF,
  ITakeSkip,
  ITypeormWhereCriteria,
  parseSearchString,
  RideEntity,
  RydeUiUnit,
  starredDisplayModeAtomFamily,
  StarredDisplayModeEnum,
  starredTableRowsIdsAtomFamily,
  TABLE_PAGE_SIZE_UNGROUPED,
  tableItemPaginationAF,
  tableRowsPaginationSf,
  tableSearchAF,
  topCalendarDatesAtomFamily,
} from "@ryde/shared";

import { buildClientSideDatesServerWhere } from "./composed-search-criteria.utils";

export const useComposedSearchCriteria = (entity: RydeUiUnit) => {
  const tableItemPagination = useRecoilValue(
    tableItemPaginationAF([GROUPING_GRID_NO_GROUPS, entity]),
  );
  const tableRowsPagination = useRecoilValue(tableRowsPaginationSf(entity));
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const searchText = useRecoilValue(tableSearchAF(entity));
  const filtersSchemaObj = useRecoilValue(filterCriteriaSF(entity));
  const isServerFuzzySearch = Boolean(fuzzyServerSearch.has(entity));
  const starredDisplayMode = useRecoilValue(
    starredDisplayModeAtomFamily(entity),
  );
  const isSearchBoxPaused = useRecoilValue(isSearchboxPausedSF(entity));
  const areFiltersPaused = useRecoilValue(areFiltersPausedSF(entity));
  const isSearchBoxIgnoringDateFilter = useRecoilValue(
    isSearchboxFilteringIgnoresDatesSF(entity),
  );
  const starredIds = useRecoilValue(starredTableRowsIdsAtomFamily(entity));

  const isStarredFilterIgnoredMode =
    starredDisplayMode === StarredDisplayModeEnum.FiltersIgnored;
  const applyStarredIdsOnServer =
    isStarredFilterIgnoredMode || isEntityServerSidePaginated(entity);

  const starredIdsWhere = useMemo(
    () =>
      applyStarredIdsOnServer
        ? getStarredDisplayModeWhere({
            mode: starredDisplayMode,
            starredIds: starredIds ?? [],
          })
        : {},
    [applyStarredIdsOnServer, starredDisplayMode, starredIds],
  );

  const getDynamicFormsComposedCriteria = useCallback((): ITypeormWhereCriteria<
    RideEntity | IExtendableDto
  > => {
    const dateQueryObject = buildTypeOrmCalendarDatesCriteria(
      entity,
      calendarDates,
    );

    if (isEntityClientSideWithServerDatesFiltered(entity)) {
      return buildClientSideDatesServerWhere({
        dateQueryObject,
        isStarredFilterIgnoredMode,
        searchText,
        starredIdsWhere,
      });
    }

    const fieldsQueryObject = filtersSchemaObj || {};
    const searchQueryObject = isServerFuzzySearch
      ? parseSearchString(getSpecificSearchPatterMatch(searchText)?.[1] ?? "")
      : {};
    const composedQueryObject = {
      ...(areFiltersPaused ? {} : fieldsQueryObject),
      ...(isSearchBoxIgnoringDateFilter ? {} : dateQueryObject),
      ...(isSearchBoxPaused ? {} : searchQueryObject),
      ...starredIdsWhere,
    } satisfies ITypeormWhereCriteria<RideEntity | IExtendableDto>;

    return composedQueryObject;
  }, [
    areFiltersPaused,
    calendarDates,
    entity,
    filtersSchemaObj,
    isSearchBoxIgnoringDateFilter,
    isSearchBoxPaused,
    isServerFuzzySearch,
    isStarredFilterIgnoredMode,
    searchText,
    starredIdsWhere,
  ]);

  const getLiveMapComposedCriteria = useEvent(
    (datesRange: DatesUnit): ITypeormWhereCriteria<RideEntity> => {
      const dateQueryObject = buildTypeOrmCalendarDatesCriteria(
        entity,
        datesRange,
      );
      const fieldsQueryObject = filtersSchemaObj || {};
      const searchQueryObject = isServerFuzzySearch
        ? parseSearchString(getSpecificSearchPatterMatch(searchText)?.[1] ?? "")
        : {};
      const composedQueryObject: ITypeormWhereCriteria<RideEntity> = {
        ...(areFiltersPaused ? {} : fieldsQueryObject),
        ...(isSearchBoxIgnoringDateFilter ? {} : dateQueryObject),
        ...(!isSearchBoxPaused ? searchQueryObject : {}),
      };
      return composedQueryObject;
    },
  );

  const getDynamicFormComposedPagination = useEvent(
    (
      groupPagination?: IPaginationChangeEvent,
      groupValuesPagination?: IGroupValuePaginationState[],
    ): ITakeSkip | ISearchCriteriaGroupValue[] => {
      if (groupPagination) {
        const { groupingValue, pagination } = groupPagination;
        return [
          {
            value: groupingValue,
            skip: pagination.pageIndex * pagination.pageSize,
            take: pagination.pageSize,
          },
        ];
      }

      if (groupValuesPagination) {
        return groupValuesPagination.map(x => ({
          value: x.value,
          skip: x.pageIndex * x.pageSize,
          take: x.pageSize,
        }));
      }

      if (tableRowsPagination.length > 0) {
        return tableRowsPagination.map(x => ({
          value: x.value,
          skip: x.pageIndex * x.pageSize,
          take: x.pageSize,
        }));
      }

      const pageIndex = tableItemPagination?.pageIndex ?? 0;
      const take = tableItemPagination?.pageSize ?? TABLE_PAGE_SIZE_UNGROUPED;
      const skip = pageIndex * take;

      return {
        take,
        skip,
      };
    },
  );

  return {
    getDynamicFormsComposedCriteria,
    getLiveMapComposedCriteria,
    getDynamicFormComposedPagination,
  };
};
